<template>
  <van-popup
    v-model:show="showModal"
    position="bottom"
    round
    class="h-[90%]"
    closeable
    @close="$emit('update:show', false)"
  >
    <div v-if="task" class="h-full flex flex-col bg-base-100">
      <!-- 头部 -->
      <div class="flex-shrink-0 px-4 py-3 border-b border-base-200">
        <div class="flex items-center gap-3">
          <div class="avatar placeholder">
            <div class="bg-base-200 text-base-content rounded-full w-10 h-10">
              <img
                v-if="task.iconUrl?.startsWith('http')"
                :src="task.iconUrl"
                alt=""
                class="w-full h-full object-cover rounded-full"
              />
              <i
                v-else-if="task.iconUrl?.startsWith('iconify')"
                :class="task.iconUrl"
              />
              <i v-else class="iconify mingcute--to-do-line" />
            </div>
          </div>
          <div class="flex-1">
            <h2 class="font-bold text-base-content mb-1">
              {{ task.title }}
            </h2>
            <div class="flex items-center gap-2">
              <div
                :class="getStatusBadgeClass(task.userStatus)"
                class="badge badge-sm"
              >
                {{ $t(`userTask.status.${task.userStatus}`) }}
              </div>
              <div
                :class="getTypeTagClass(task.type)"
                class="badge badge-outline badge-sm"
              >
                {{ $t(`userTask.type.${task.type}`) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="flex-1 overflow-y-auto p-2 space-y-2" ref="contentRef">
        <!-- 任务描述 -->
        <div class="card bg-base-200">
          <div class="card-body p-4 gap-2">
            <h3 class="font-semibold">任务简介</h3>
            <p class="text-xs text-base-content/80">{{ task.description }}</p>
          </div>
        </div>

        <!-- 详细描述 -->
        <div class="card bg-base-200">
          <div class="card-body p-4 gap-2">
            <div class="flex items-center justify-between">
              <h3 class="font-semibold">详细说明</h3>

              <button
                class="btn btn-xs btn-primary"
                v-if="
                  task.action && task.userStatus === UserTaskStatus.IN_PROGRESS
                "
                @click="clickAction"
              >
                前往完成
              </button>
            </div>
            <div class="text-xs text-base-content/80 whitespace-pre-line">
              {{ task.detailedDescription }}
            </div>
          </div>
        </div>

        <!-- 任务进度 -->
        <!-- <div
          v-if="task.userStatus === UserTaskStatus.IN_PROGRESS"
          class="card bg-base-200"
        >
          <div class="card-body p-4 gap-3">
            <div class="flex items-center justify-between">
              <h3 class="font-semibold">任务进度</h3>

              <button
                class="btn btn-xs btn-primary"
                v-if="task.action"
                @click="clickAction"
              >
                前往完成
              </button>
            </div>
            <div class="space-y-2">
              <van-progress :percentage="task.progress" stroke-width="8" />
            </div>
          </div>
        </div> -->

        <!-- 任务奖励 -->
        <div class="card bg-base-200">
          <div class="card-body p-4 gap-1">
            <h3 class="font-semibold">任务奖励</h3>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="reward in task.rewards"
                :key="reward.type"
                class="flex items-center gap-2 bg-base-100 p-3 rounded-lg"
              >
                <div
                  class="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center"
                >
                  <power-icon v-if="reward.type === 'powerCube'" />
                  <crystal-icon v-if="reward.type === 'crystal'" />
                  <!-- <equipment-icon
                    v-if="reward.type === 'equipment'"
                    :equipment="reward.equipment"
                  /> -->
                </div>
                <div v-if="reward.amount" class="text-base">
                  ×{{ reward.amount }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 任务时间 -->
        <div
          v-if="
            task.dtStart && task.dtEnd && task.type === UserTaskType.ACTIVITY
          "
          class="card bg-base-200"
        >
          <div class="card-body p-4 gap-1">
            <h3 class="font-semibold">任务时间</h3>
            <div class="space-y-1 text-xs">
              <div v-if="task.dtStart" class="flex justify-between">
                <span class="text-base-content/70">开始时间：</span>
                <span>{{ formatDate(task.dtStart) }}</span>
              </div>
              <div v-if="task.dtEnd" class="flex justify-between">
                <span class="text-base-content/70">结束时间：</span>
                <span>{{ formatDate(task.dtEnd) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部操作区 -->
      <div class="flex-shrink-0 p-4 border-t border-base-200 bg-base-100">
        <div class="flex gap-3">
          <button
            v-if="task.userStatus === UserTaskStatus.COMPLETED"
            class="btn btn-success flex-1"
            @click="handleClaim"
          >
            <van-icon name="gift-o" class="mr-1" />
            领取奖励
          </button>
          <button
            v-else-if="task.userStatus === UserTaskStatus.CLAIMED"
            class="btn btn-ghost flex-1"
            disabled
          >
            <van-icon name="success" class="mr-1" />
            已完成
          </button>
          <button
            v-else
            class="btn btn-ghost flex-1"
            @click="$emit('update:show', false)"
          >
            关闭
          </button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="h-full flex items-center justify-center">
      <div class="loading loading-spinner loading-lg"></div>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { UserTask } from "@/types/entity";
import { UserTaskStatus, UserTaskType } from "@/enums/mateEnums";
import { useRouter } from "vue-router";
import { getStatusBadgeClass, getTypeTagClass } from "./TaskStyle";
import { useUI } from "@/stores/ui";
import { useGlobalModal } from "@/stores/globalModal";

const props = defineProps<{
  show: boolean;
  task: UserTask | null;
}>();

const emit = defineEmits<{
  "update:show": [value: boolean];
  claim: [id: number];
}>();

const router = useRouter();

const contentRef = ref<HTMLElement | null>(null);

const showModal = computed({
  get: () => {
    if (props.show) {
      contentRef.value?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    return props.show;
  },
  set: (value) => emit("update:show", value),
});

const handleClaim = () => {
  if (props.task) {
    emit("claim", props.task.id!);
  }
};

const modalStore = useGlobalModal();

const clickAction = () => {
  const { action } = props.task!;
  emit("update:show", false);
  if (!action) return;

  modalStore.showTaskPageModal = false;

  if (action?.startsWith("/")) return router.push(action);

  if (action === "createMate") {
    useUI().popMateSelector({
      mateOnly: true,
      switchMate: false,
      showUnpublished: true,
      callback: (mate) => {
        router.push({
          name: "mateEdit",
          params: {
            uuid: mate.uuid,
          },
        });
      },
    });
  }
};

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};
</script>
